/**
 * Host-aware SPA roles — Item 2 phases:
 * 1 foundation — classify hostname
 * 2 wire — Landing vs Gateway vs App
 * 3 prove — local override + absolute cross-domain CTAs
 */

export type HostRole = 'marketing' | 'gateway' | 'app' | 'vanity' | 'local' | 'other'

export type HostMode = {
  role: HostRole
  hostname: string
  vanitySlug: string | null
  marketingUrl: string
  gatewayUrl: string
  appUrl: string
  /** Prefer dense product CTAs (gateway/app/vanity). */
  isProductEntry: boolean
  /** Long-form persuasion landing. */
  isMarketing: boolean
}

const RESERVED_AI = new Set([
  'www',
  'app',
  'api',
  'status',
  'docs',
  'cdn',
  'static',
  'mail',
  'admin',
  'auth',
  'login',
  'signup',
  'go',
  'preview',
  'eph',
  'mcp',
])

function env(name: string, fallback: string): string {
  const meta = import.meta as ImportMeta & {
    env?: Record<string, string | undefined>
  }
  return (meta.env?.[name] || fallback).replace(/\/$/, '')
}

export function marketingBaseUrl(): string {
  return env('VITE_AFOS360_MARKETING_URL', 'https://afos360.com')
}

export function gatewayBaseUrl(): string {
  return env('VITE_AFOS360_GATEWAY_URL', 'https://afos360.ai')
}

export function appBaseUrl(): string {
  return env('VITE_AFOS360_APP_URL', 'https://app.afos360.ai')
}

export function classifyHostname(hostname: string): {
  role: HostRole
  vanitySlug: string | null
} {
  const host = (hostname || '').toLowerCase().split(':')[0]
  const override = env('VITE_AFOS360_HOST_MODE', '')
  if (
    override === 'marketing' ||
    override === 'gateway' ||
    override === 'app' ||
    override === 'vanity' ||
    override === 'local'
  ) {
    return {
      role: override,
      vanitySlug: override === 'vanity' ? 'demo' : null,
    }
  }

  if (!host || host === 'localhost' || host === '127.0.0.1') {
    return { role: 'local', vanitySlug: null }
  }

  if (host === 'afos360.com' || host === 'www.afos360.com') {
    return { role: 'marketing', vanitySlug: null }
  }
  if (host.endsWith('.afos360.com')) {
    return { role: 'other', vanitySlug: null }
  }
  if (host === 'app.afos360.ai') {
    return { role: 'app', vanitySlug: null }
  }
  if (host === 'afos360.ai' || host === 'www.afos360.ai') {
    return { role: 'gateway', vanitySlug: null }
  }
  if (host.endsWith('.afos360.ai')) {
    const label = host.slice(0, -'.afos360.ai'.length)
    if (!label || label.includes('.') || RESERVED_AI.has(label)) {
      return { role: label === 'app' ? 'app' : 'other', vanitySlug: null }
    }
    return { role: 'vanity', vanitySlug: label }
  }
  return { role: 'other', vanitySlug: null }
}

export function resolveHostMode(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : 'localhost',
): HostMode {
  const { role, vanitySlug } = classifyHostname(hostname)
  const marketingUrl = marketingBaseUrl()
  const gatewayUrl = gatewayBaseUrl()
  const appUrl = appBaseUrl()
  const isMarketing = role === 'marketing'
  const isProductEntry =
    role === 'gateway' || role === 'app' || role === 'vanity' || role === 'local'
  return {
    role,
    hostname,
    vanitySlug,
    marketingUrl,
    gatewayUrl,
    appUrl,
    isProductEntry,
    isMarketing,
  }
}

/** Absolute app signup — used from .com so trial activates on .ai */
export function absoluteSignupUrl(mode: HostMode, src = 'com'): string {
  if (mode.role === 'local' || mode.role === 'app' || mode.role === 'vanity') {
    return `/signup?src=${encodeURIComponent(src)}`
  }
  return `${mode.appUrl}/signup?src=${encodeURIComponent(src)}`
}

export function absoluteSigninUrl(mode: HostMode, src = 'com'): string {
  if (mode.role === 'local' || mode.role === 'app' || mode.role === 'vanity') {
    return `/signin?src=${encodeURIComponent(src)}`
  }
  return `${mode.appUrl}/signin?src=${encodeURIComponent(src)}`
}

export function marketingSiteUrl(mode: HostMode): string {
  if (mode.role === 'local' || mode.role === 'marketing') return '/'
  return mode.marketingUrl
}
